import { useState, useEffect, Fragment } from 'react';
import { motion } from 'framer-motion';
import {
  listDisplayCases,
  createDisplayCase,
  updateDisplayCase,
  deleteDisplayCase,
  listDisplayCasePurchases,
  updateDisplayCasePurchaseStatus,
  getDisplayCaseSettings,
  updateDisplayCaseSettings,
  type DisplayCase,
  type DisplayCasePurchase,
  type DisplayCaseProgramSettings,
} from '../../services/adminApi';

type Tab = 'catalog' | 'purchases' | 'settings';

export default function AdminDisplayCases() {
  const [activeTab, setActiveTab] = useState<Tab>('catalog');

  const tabs: { key: Tab; label: string }[] = [
    { key: 'catalog', label: 'Catalog' },
    { key: 'purchases', label: 'Purchases' },
    { key: 'settings', label: 'Settings' },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-display text-cream tracking-wide">
          Display Cases
        </h2>
        <p className="text-sm text-text-muted mt-1">
          Manage display case catalog, customer purchases, and program settings
        </p>
      </div>

      {/* Sub-tabs */}
      <div className="flex space-x-4 border-b border-white/10">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`pb-2 px-1 text-sm font-medium uppercase tracking-wide border-b-2 transition-colors ${
              activeTab === tab.key
                ? 'border-brass text-brass'
                : 'border-transparent text-text-muted hover:text-cream'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'catalog' && <CatalogTab />}
      {activeTab === 'purchases' && <PurchasesTab />}
      {activeTab === 'settings' && <SettingsTab />}
    </div>
  );
}

// ============================================
// Catalog Tab
// ============================================

function CatalogTab() {
  const [cases, setCases] = useState<DisplayCase[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingCase, setEditingCase] = useState<DisplayCase | null>(null);

  const fetchCases = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await listDisplayCases();
      setCases(response.cases);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCases(); }, []);

  const handleDelete = async (caseId: string) => {
    if (!confirm('Delete this display case?')) return;
    try {
      await deleteDisplayCase(caseId);
      await fetchCases();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete');
    }
  };

  const handleEdit = (dc: DisplayCase) => {
    setEditingCase(dc);
    setShowModal(true);
  };

  const handleAdd = () => {
    setEditingCase(null);
    setShowModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setEditingCase(null);
  };

  const handleModalSave = async () => {
    handleModalClose();
    await fetchCases();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[30vh]">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
          className="w-10 h-10 border-4 border-workshop-elevated border-t-accent rounded-full"
        />
      </div>
    );
  }

  return (
    <div>
      {error && (
        <div className="mb-4 p-3 bg-red-950/50 border border-red-800/30 rounded-lg text-red-300 text-sm">
          {error}
        </div>
      )}

      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-text-muted">{cases.length} display case(s)</p>
        <button
          onClick={handleAdd}
          className="px-4 py-2 bg-accent text-workshop rounded-sm hover:bg-accent-light transition-colors uppercase tracking-wide text-sm font-medium"
        >
          + Add Display Case
        </button>
      </div>

      {cases.length === 0 ? (
        <div className="card text-center py-12 text-text-muted">
          No display cases yet. Add one to get started.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {cases.map((dc) => (
            <div key={dc.caseId} className="card">
              {dc.imageUrl && (
                <img
                  src={dc.imageUrl}
                  alt={dc.name}
                  className="w-full h-48 object-cover rounded-sm mb-3"
                />
              )}
              <div className="flex justify-between items-start mb-2">
                <h3 className="font-medium text-cream">{dc.name}</h3>
                <span
                  className={`text-xs px-2 py-0.5 rounded-sm font-medium ${
                    dc.active
                      ? 'bg-green-900/40 text-green-300'
                      : 'bg-white/5 text-text-muted'
                  }`}
                >
                  {dc.active ? 'Active' : 'Inactive'}
                </span>
              </div>
              {dc.description && (
                <p className="text-sm text-text-muted mb-2">{dc.description}</p>
              )}
              <div className="text-sm space-y-1 mb-3">
                <p>
                  <span className="text-text-muted">Price:</span>{' '}
                  <span className="font-semibold text-accent">
                    ${dc.price.toFixed(2)}
                  </span>
                </p>
                {dc.dimensions && (
                  <p>
                    <span className="text-text-muted">Dimensions:</span>{' '}
                    {dc.dimensions}
                  </p>
                )}
                <p>
                  <span className="text-text-muted">Sort Order:</span>{' '}
                  {dc.sortOrder}
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => handleEdit(dc)}
                  className="flex-1 px-3 py-1.5 text-xs uppercase tracking-wide text-accent border border-accent rounded-sm hover:bg-accent hover:text-workshop transition-colors"
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(dc.caseId)}
                  className="px-3 py-1.5 text-xs uppercase tracking-wide text-red-400 border border-red-700/30 rounded-sm hover:bg-red-900/20 transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <CaseModal
          editingCase={editingCase}
          onClose={handleModalClose}
          onSave={handleModalSave}
        />
      )}
    </div>
  );
}

// ============================================
// Case Modal
// ============================================

function CaseModal({
  editingCase,
  onClose,
  onSave,
}: {
  editingCase: DisplayCase | null;
  onClose: () => void;
  onSave: () => void;
}) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({
    name: editingCase?.name || '',
    description: editingCase?.description || '',
    price: editingCase?.price ?? 0,
    imageUrl: editingCase?.imageUrl || '',
    dimensions: editingCase?.dimensions || '',
    active: editingCase?.active !== false,
    sortOrder: editingCase?.sortOrder ?? 0,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name || form.price <= 0) {
      setError('Name and a positive price are required');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      if (editingCase) {
        await updateDisplayCase(editingCase.caseId, form);
      } else {
        await createDisplayCase(form);
      }
      onSave();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-workshop-surface rounded-lg shadow-xl border border-white/[0.06] max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <h3 className="text-lg font-display text-cream mb-4">
            {editingCase ? 'Edit Display Case' : 'Add Display Case'}
          </h3>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-cream mb-1">Name *</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full px-3 py-2 border border-white/10 rounded-lg bg-workshop text-text focus:outline-none focus:ring-2 focus:ring-accent/50"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-cream mb-1">Description</label>
              <textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 border border-white/10 rounded-lg bg-workshop text-text focus:outline-none focus:ring-2 focus:ring-accent/50"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-cream mb-1">Price ($) *</label>
                <input
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={form.price}
                  onChange={(e) => setForm({ ...form, price: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-white/10 rounded-lg bg-workshop text-text focus:outline-none focus:ring-2 focus:ring-accent/50"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-cream mb-1">Sort Order</label>
                <input
                  type="number"
                  value={form.sortOrder}
                  onChange={(e) => setForm({ ...form, sortOrder: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-white/10 rounded-lg bg-workshop text-text focus:outline-none focus:ring-2 focus:ring-accent/50"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-cream mb-1">Dimensions</label>
              <input
                type="text"
                value={form.dimensions}
                onChange={(e) => setForm({ ...form, dimensions: e.target.value })}
                placeholder='e.g. 24" x 18" x 12"'
                className="w-full px-3 py-2 border border-white/10 rounded-lg bg-workshop text-text focus:outline-none focus:ring-2 focus:ring-accent/50"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-cream mb-1">Image URL</label>
              <input
                type="url"
                value={form.imageUrl}
                onChange={(e) => setForm({ ...form, imageUrl: e.target.value })}
                placeholder="https://..."
                className="w-full px-3 py-2 border border-white/10 rounded-lg bg-workshop text-text focus:outline-none focus:ring-2 focus:ring-accent/50"
              />
              <p className="text-xs text-text-muted mt-1">
                Paste the URL of the image hosted on S3/CloudFront
              </p>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="active"
                checked={form.active}
                onChange={(e) => setForm({ ...form, active: e.target.checked })}
                className="rounded border-white/10"
              />
              <label htmlFor="active" className="text-sm text-cream">
                Active (visible to customers)
              </label>
            </div>

            {error && (
              <div className="p-3 bg-red-950/50 border border-red-800/30 rounded-lg text-red-300 text-sm">
                {error}
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 text-sm text-cream border border-white/10 rounded-lg hover:bg-white/5 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="flex-1 px-4 py-2 text-sm bg-accent text-workshop rounded-lg hover:bg-accent-light disabled:opacity-50 transition-colors uppercase tracking-wide font-medium"
              >
                {saving ? 'Saving...' : editingCase ? 'Update' : 'Create'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// ============================================
// Purchases Tab
// ============================================

function PurchasesTab() {
  const [purchases, setPurchases] = useState<DisplayCasePurchase[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedRow, setExpandedRow] = useState<string | null>(null);

  const fetchPurchases = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await listDisplayCasePurchases();
      setPurchases(response.purchases);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPurchases(); }, []);

  const handleAction = async (
    purchase: DisplayCasePurchase,
    action: 'approve' | 'activate' | 'cancel'
  ) => {
    const sk = `${purchase.cognitoSub}#${purchase.caseId}`;
    try {
      await updateDisplayCasePurchaseStatus(sk, action);
      await fetchPurchases();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update');
    }
  };

  const statusColors: Record<string, string> = {
    requested: 'bg-yellow-900/40 text-yellow-300',
    approved: 'bg-blue-900/40 text-blue-300',
    active: 'bg-green-900/40 text-green-300',
    completed: 'bg-purple-900/40 text-purple-300',
    cancelled: 'bg-white/5 text-text-muted',
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[30vh]">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
          className="w-10 h-10 border-4 border-workshop-elevated border-t-accent rounded-full"
        />
      </div>
    );
  }

  return (
    <div>
      {error && (
        <div className="mb-4 p-3 bg-red-950/50 border border-red-800/30 rounded-lg text-red-300 text-sm">
          {error}
        </div>
      )}

      {purchases.length === 0 ? (
        <div className="card text-center py-12 text-text-muted">
          No display case purchases yet.
        </div>
      ) : (
        <div className="card overflow-hidden p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-leather text-cream text-left">
                  <th className="px-4 py-3 text-xs uppercase tracking-wide font-semibold">Customer</th>
                  <th className="px-4 py-3 text-xs uppercase tracking-wide font-semibold">Company</th>
                  <th className="px-4 py-3 text-xs uppercase tracking-wide font-semibold">Case</th>
                  <th className="px-4 py-3 text-xs uppercase tracking-wide font-semibold text-right">Price</th>
                  <th className="px-4 py-3 text-xs uppercase tracking-wide font-semibold text-center">Status</th>
                  <th className="px-4 py-3 text-xs uppercase tracking-wide font-semibold">Rebate Progress</th>
                  <th className="px-4 py-3 text-xs uppercase tracking-wide font-semibold text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {purchases.map((p) => {
                  const sk = `${p.cognitoSub}#${p.caseId}`;
                  const isExpanded = expandedRow === sk;
                  const progressPct = p.purchasePrice > 0
                    ? Math.min(100, (p.totalRebateEarned / p.purchasePrice) * 100)
                    : 0;

                  return (
                    <Fragment key={sk}>
                      <tr
                        className="border-b border-white/[0.06] hover:bg-white/5 cursor-pointer"
                        onClick={() => setExpandedRow(isExpanded ? null : sk)}
                      >
                        <td className="px-4 py-3 text-sm">{p.customerEmail}</td>
                        <td className="px-4 py-3 text-sm text-text-muted">{p.companyName || '-'}</td>
                        <td className="px-4 py-3 text-sm font-medium">{p.caseName}</td>
                        <td className="px-4 py-3 text-sm text-right font-mono">${p.purchasePrice.toFixed(2)}</td>
                        <td className="px-4 py-3 text-center">
                          <span className={`inline-flex px-2 py-0.5 text-xs rounded-sm font-medium ${statusColors[p.status] || ''}`}>
                            {p.status}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="flex-1 bg-white/10 rounded-full h-2 min-w-[80px]">
                              <div
                                className="bg-brass h-2 rounded-full transition-all"
                                style={{ width: `${progressPct}%` }}
                              />
                            </div>
                            <span className="text-xs text-text-muted whitespace-nowrap">
                              ${p.totalRebateEarned.toFixed(2)} / ${p.purchasePrice.toFixed(2)}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <div className="flex gap-1 justify-center" onClick={(e) => e.stopPropagation()}>
                            {p.status === 'requested' && (
                              <button
                                onClick={() => handleAction(p, 'approve')}
                                className="px-2 py-1 text-xs text-green-300 bg-green-900/20 border border-green-700/30 rounded-sm hover:bg-green-900/30"
                              >
                                Approve
                              </button>
                            )}
                            {p.status === 'approved' && (
                              <button
                                onClick={() => handleAction(p, 'activate')}
                                className="px-2 py-1 text-xs text-blue-300 bg-blue-900/20 border border-blue-700/30 rounded-sm hover:bg-blue-900/30"
                              >
                                Activate
                              </button>
                            )}
                            {(p.status === 'requested' || p.status === 'approved') && (
                              <button
                                onClick={() => {
                                  if (confirm(`Cancel display case purchase for ${p.customerEmail}?`)) {
                                    handleAction(p, 'cancel');
                                  }
                                }}
                                className="px-2 py-1 text-xs text-red-300 bg-red-900/20 border border-red-700/30 rounded-sm hover:bg-red-900/30"
                              >
                                Cancel
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                      {isExpanded && p.rebates && p.rebates.length > 0 && (
                        <tr>
                          <td colSpan={7} className="bg-white/5 px-8 py-4">
                            <p className="text-xs uppercase tracking-wide text-text-muted mb-2 font-medium">
                              Rebate History
                            </p>
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="text-left text-text-muted">
                                  <th className="py-1 text-xs">Date</th>
                                  <th className="py-1 text-xs">Order</th>
                                  <th className="py-1 text-xs text-right">Order Total</th>
                                  <th className="py-1 text-xs text-right">Rebate %</th>
                                  <th className="py-1 text-xs text-right">Earned</th>
                                </tr>
                              </thead>
                              <tbody>
                                {p.rebates.map((r, idx) => (
                                  <tr key={idx} className="border-t border-white/[0.06]">
                                    <td className="py-1.5">{new Date(r.createdAt).toLocaleDateString()}</td>
                                    <td className="py-1.5">{r.orderName}</td>
                                    <td className="py-1.5 text-right font-mono">${r.orderTotal.toFixed(2)}</td>
                                    <td className="py-1.5 text-right">{r.rebatePercent}%</td>
                                    <td className="py-1.5 text-right font-mono text-green-400">
                                      ${r.cappedAmount.toFixed(2)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </td>
                        </tr>
                      )}
                      {isExpanded && (!p.rebates || p.rebates.length === 0) && (
                        <tr>
                          <td colSpan={7} className="bg-white/5 px-8 py-4 text-sm text-text-muted">
                            No rebates recorded yet.
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================
// Settings Tab
// ============================================

function SettingsTab() {
  const [settings, setSettings] = useState<DisplayCaseProgramSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    active: false,
    rebatePercent: 20,
    rebateDescription: '',
  });

  const fetchSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getDisplayCaseSettings();
      setSettings(response.settings);
      setFormData({
        active: response.settings.active,
        rebatePercent: response.settings.rebatePercent,
        rebateDescription: response.settings.rebateDescription,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSettings(); }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      await updateDisplayCaseSettings(formData);
      setSuccess('Display case program settings saved');
      await fetchSettings();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[30vh]">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
          className="w-10 h-10 border-4 border-workshop-elevated border-t-accent rounded-full"
        />
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="max-w-2xl">
      <div className="card space-y-6">
        {/* Program Active Toggle */}
        <div className="flex items-center justify-between">
          <div>
            <label className="block text-sm font-medium text-cream">
              Program Active
            </label>
            <p className="text-xs text-text-muted mt-0.5">
              When enabled, customers can view and request display cases
            </p>
          </div>
          <button
            type="button"
            onClick={() => setFormData((prev) => ({ ...prev, active: !prev.active }))}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              formData.active ? 'bg-brass' : 'bg-white/20'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                formData.active ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>

        {/* Rebate Percentage */}
        <div>
          <label className="block text-sm font-medium text-cream mb-2">
            Rebate Percentage (%)
          </label>
          <input
            type="number"
            min="0"
            max="100"
            step="1"
            value={formData.rebatePercent}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                rebatePercent: parseFloat(e.target.value) || 0,
              }))
            }
            className="w-full px-4 py-2 border border-white/10 rounded-lg bg-workshop text-text focus:outline-none focus:ring-2 focus:ring-accent/50"
          />
          <p className="text-xs text-text-muted mt-1">
            Percentage of each reorder total applied toward display case rebate
          </p>
        </div>

        {/* Rebate Description */}
        <div>
          <label className="block text-sm font-medium text-cream mb-2">
            Program Description
          </label>
          <textarea
            value={formData.rebateDescription}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, rebateDescription: e.target.value }))
            }
            rows={3}
            className="w-full px-4 py-2 border border-white/10 rounded-lg bg-workshop text-text focus:outline-none focus:ring-2 focus:ring-accent/50"
          />
          <p className="text-xs text-text-muted mt-1">
            Displayed to customers on the display case program page
          </p>
        </div>

        {/* Error/Success */}
        {error && (
          <div className="p-3 bg-red-950/50 border border-red-800/30 rounded-lg text-red-300 text-sm">
            {error}
          </div>
        )}
        {success && (
          <div className="p-3 bg-green-950/50 border border-green-800/30 rounded-lg text-green-300 text-sm">
            {success}
          </div>
        )}

        {/* Submit */}
        <div className="flex items-center justify-between pt-4 border-t border-white/[0.06]">
          <div className="text-xs text-text-muted">
            {settings?.updatedAt && (
              <>
                Last updated: {new Date(settings.updatedAt).toLocaleString()}
                {settings.updatedBy && ` by ${settings.updatedBy}`}
              </>
            )}
          </div>
          <button
            type="submit"
            disabled={saving}
            className="px-6 py-2 bg-accent text-workshop rounded-lg hover:bg-accent-light disabled:opacity-50 transition-colors uppercase tracking-wide text-sm font-medium"
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>
    </form>
  );
}
